import "@xyflow/react/dist/style.css";

import dagre from "@dagrejs/dagre";
import {
  Background,
  Controls,
  type Edge,
  type Node,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import type { CSSProperties } from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { EmptyState } from "~/components/empty-state";

import type { EntityLineage, LineageEdgeType, LineageNodeType } from "~/features/entities/types";

/** Edge payload carried into ReactFlow for click-through navigation. */
interface LineageEdgeData {
  type: LineageEdgeType;
  projectID?: string;
  changeRequestJobID?: string;
  evidenceJobID?: string;
}

const NODE_W = 180;
const NODE_H = 48;

const ROUTE_BY_TYPE: Record<LineageNodeType, string> = {
  entity: "/entities",
  changeRequest: "/change-requests",
  evidence: "/evidences",
  reference: "/references",
};

const STYLE_BY_TYPE: Record<LineageNodeType, CSSProperties> = {
  entity: { background: "var(--color-primary-50)", borderColor: "var(--color-primary-300)" },
  changeRequest: { background: "var(--color-bg-subtle)", borderColor: "var(--color-border-strong)" },
  evidence: { background: "var(--color-score-a-bg)", borderColor: "var(--color-border-default)" },
  reference: { background: "var(--color-bg-tint)", borderColor: "var(--color-border-default)" },
};

const MUTED_STYLE: CSSProperties = { opacity: 0.5 };

interface EntityLineageTabProps {
  lineage: EntityLineage;
}

export function EntityLineageTab({ lineage }: EntityLineageTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const laid = useMemo(() => layout(lineage), [lineage]);
  const [nodes, setNodes, onNodesChange] = useNodesState(laid.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(laid.edges);

  // Re-seed the graph state when the underlying lineage changes (e.g. navigating between entities).
  useEffect(() => {
    setNodes(laid.nodes);
    setEdges(laid.edges);
  }, [laid, setNodes, setEdges]);

  if (lineage.nodes.length <= 1) {
    return <EmptyState title={t("ui.lineage")} description={t("feature.common.noData")} />;
  }

  return (
    <div className="h-[60vh] overflow-hidden rounded-[var(--radius-md)] border border-border-default bg-white">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        onNodeClick={(_, node) => {
          const data = node.data as { id: string; type: LineageNodeType };
          navigate(`${ROUTE_BY_TYPE[data.type]}/${data.id}`);
        }}
        onEdgeClick={(_, edge) => {
          // Mirrors V1: open the data-import job that produced this lineage link, as a peek
          // on the DIP pipeline. Other edge kinds (CR→record, evidence→evidence) are inert.
          const data = edge.data as LineageEdgeData | undefined;
          if (!data?.projectID) return;
          if (data.type === "evidenceToChangeRequest" && data.changeRequestJobID) {
            const qs = new URLSearchParams({ peekType: "batchChangeRequestsJob", peekId: data.changeRequestJobID });
            navigate(`/data-import-projects/${data.projectID}?${qs}`);
          } else if (data.type === "referenceToEvidence" && data.evidenceJobID) {
            const qs = new URLSearchParams({ peekType: "batchEvidencesJob", peekId: data.evidenceJobID });
            navigate(`/data-import-projects/${data.projectID}?${qs}`);
          }
        }}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}

/** Lay the lineage graph out left-to-right with dagre. */
function layout(lineage: EntityLineage): { nodes: Node[]; edges: Edge[] } {
  const graph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: "LR" });
  for (const n of lineage.nodes) graph.setNode(n.id, { width: NODE_W, height: NODE_H });
  for (const e of lineage.edges) graph.setEdge(e.source, e.target);
  dagre.layout(graph);

  const nodes: Node[] = lineage.nodes.map((n) => {
    const pos = graph.node(n.id);
    return {
      id: n.id,
      data: {
        id: n.id,
        type: n.type,
        label: (
          <div className="leading-tight">
            <div className="text-12-bold text-fg-1">{n.label}</div>
            <div className="truncate text-[10px] text-fg-3">{n.subLabel}</div>
          </div>
        ),
      },
      position: { x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 },
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      style: {
        width: NODE_W,
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 8,
        ...STYLE_BY_TYPE[n.type],
        ...(n.muted ? MUTED_STYLE : {}),
      },
    };
  });

  const edges: Edge[] = lineage.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: true,
    data: {
      type: e.type,
      projectID: e.projectID,
      changeRequestJobID: e.changeRequestJobID,
      evidenceJobID: e.evidenceJobID,
    } satisfies LineageEdgeData,
  }));
  return { nodes, edges };
}
